import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export interface MentorTopic {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MentorTopicSelection {
  mentor_id: string;
  topic_id: string;
  created_at: string;
}

// Fetch all active mentor topics
export function useMentorTopics() {
  return useQuery({
    queryKey: ["mentor-topics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mentor_topics")
        .select("*")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      return data as MentorTopic[];
    },
  });
}

// Fetch all mentor topics (including inactive) for admin
export function useAllMentorTopics() {
  return useQuery({
    queryKey: ["mentor-topics-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mentor_topics")
        .select("*")
        .order("name");

      if (error) throw error;
      return data as MentorTopic[];
    },
  });
}

// Fetch current user's topic selections
export function useMentorTopicSelections() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["mentor-topic-selections", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mentor_topic_selections")
        .select("*")
        .eq("mentor_id", user!.id);

      if (error) throw error;
      return data as MentorTopicSelection[];
    },
  });
}

// Toggle a topic selection for the current user
export function useToggleMentorTopicSelection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ topicId, isSelected }: { topicId: string; isSelected: boolean }) => {
      if (!user?.id) throw new Error("Not authenticated");

      if (isSelected) {
        // Add selection
        const { error } = await supabase
          .from("mentor_topic_selections")
          .insert({ mentor_id: user.id, topic_id: topicId });
        if (error) throw error;
      } else {
        // Remove selection
        const { error } = await supabase
          .from("mentor_topic_selections")
          .delete()
          .eq("mentor_id", user.id)
          .eq("topic_id", topicId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentor-topic-selections"] });
    },
    onError: (error) => {
      toast.error("Failed to update topic selection");
      console.error(error);
    },
  });
}

// Admin: Create a new mentor topic
export function useCreateMentorTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, description }: { name: string; description?: string }) => {
      const { data, error } = await supabase
        .from("mentor_topics")
        .insert({ name, description })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentor-topics"] });
      queryClient.invalidateQueries({ queryKey: ["mentor-topics-all"] });
      toast.success("Topic created successfully");
    },
    onError: (error: Error) => {
      if (error.message.includes("duplicate")) {
        toast.error("A topic with this name already exists");
      } else {
        toast.error("Failed to create topic");
      }
    },
  });
}

// Admin: Update a mentor topic
export function useUpdateMentorTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, name, description, is_active }: { id: string; name?: string; description?: string; is_active?: boolean }) => {
      const updates: Record<string, unknown> = {};
      if (name !== undefined) updates.name = name;
      if (description !== undefined) updates.description = description;
      if (is_active !== undefined) updates.is_active = is_active;

      const { data, error } = await supabase
        .from("mentor_topics")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentor-topics"] });
      queryClient.invalidateQueries({ queryKey: ["mentor-topics-all"] });
      toast.success("Topic updated successfully");
    },
    onError: (error: Error) => {
      if (error.message.includes("duplicate")) {
        toast.error("A topic with this name already exists");
      } else {
        toast.error("Failed to update topic");
      }
    },
  });
}

// Admin: Delete a mentor topic
export function useDeleteMentorTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("mentor_topics")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentor-topics"] });
      queryClient.invalidateQueries({ queryKey: ["mentor-topics-all"] });
      toast.success("Topic deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete topic. It may be in use.");
    },
  });
}
